'use client';

import { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import Script from 'next/script';

declare global {
  interface Window {
    turnstile?: {
      render: (element: string | HTMLElement, options: Record<string, unknown>) => string;
      reset: (widgetId: string) => void;
    };
  }
}

export default function ContactPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const turnstileRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (window.turnstile && turnstileRef.current && !widgetIdRef.current) {
      widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
        sitekey: process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY,
        callback: (token: string) => setTurnstileToken(token),
      });
    }
  }, []);

  function handleTurnstileLoad() {
    if (window.turnstile && turnstileRef.current && !widgetIdRef.current) {
      widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
        sitekey: process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY,
        callback: (token: string) => setTurnstileToken(token),
      });
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setStatus('submitting');
    setErrorMsg('');

    if (!turnstileToken) {
      setErrorMsg('Please complete the verification check.');
      setStatus('error');
      return;
    }

    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name, email, phone, message,
          token: turnstileToken,
          website: (document.getElementById('website') as HTMLInputElement)?.value || '',
        }),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to send message');
      }

      setStatus('success');
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Something went wrong');
      setStatus('error');
      // Reset turnstile on error so user can retry
      if (window.turnstile && widgetIdRef.current) {
        window.turnstile.reset(widgetIdRef.current);
        setTurnstileToken('');
      }
    }
  }

  return (
    <div className="min-h-screen bg-base-100">
      <Script
        src="https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onloadTurnstileCallback"
        strategy="afterInteractive"
        onLoad={handleTurnstileLoad}
      />
      {/* Header */}
      <header className="sticky top-0 z-50 bg-base-100/80 backdrop-blur-md border-b border-base-200/50 shadow-sm">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center h-16">
            <Link href="/" className="flex items-center gap-2">
              <Image
                src="/general/MMP Color Logo.png"
                alt="ChangeLyric"
                width={32}
                height={32}
                className="rounded-full object-cover"
              />
              <span className="text-xl font-bold text-primary hidden sm:inline">
                ChangeLyric
              </span>
            </Link>
            <nav className="flex items-center gap-2">
              <Link href="/" className="btn btn-ghost btn-sm">Home</Link>
              <Link href="/contact" className="btn btn-ghost btn-sm btn-active">Contact</Link>
            </nav>
          </div>
        </div>
      </header>

      <main className="py-16 sm:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-lg mx-auto">
            <h1 className="text-3xl sm:text-4xl font-extrabold text-center mb-2">
              Contact Us
            </h1>
            <p className="text-base-content/70 text-center mb-8">
              Have a question or need help? Send us a message and we will get back to you quickly.
            </p>

            {status === 'success' ? (
              <div className="card bg-base-200 shadow-xl">
                <div className="card-body text-center">
                  <svg className="w-14 h-14 text-success mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <h2 className="text-2xl font-bold">Message Sent!</h2>
                  <p className="text-base-content/70">
                    Thanks for reaching out. We will get back to you as soon as possible.
                  </p>
                  <button className="btn btn-ghost btn-sm mt-4" onClick={() => { setStatus('idle'); setName(''); setEmail(''); setPhone(''); setMessage(''); setTurnstileToken(''); if (window.turnstile && widgetIdRef.current) { window.turnstile.reset(widgetIdRef.current); } }}>
                    Send another message
                  </button>
                </div>
              </div>
            ) : (
              <div className="card bg-base-200 shadow-xl">
                <div className="card-body">
                  <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Honeypot - hidden from humans, bots fill it */}
                    <div className="absolute opacity-0 top-0 left-0 h-0 w-0 -z-10" aria-hidden="true">
                      <label htmlFor="website">Website</label>
                      <input
                        type="text"
                        id="website"
                        name="website"
                        tabIndex={-1}
                        autoComplete="off"
                      />
                    </div>

                    <div>
                      <label className="label">
                        <span className="label-text font-medium">Name *</span>
                      </label>
                      <input
                        type="text"
                        className="input input-bordered w-full"
                        placeholder="Your name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                      />
                    </div>

                    <div>
                      <label className="label">
                        <span className="label-text font-medium">Email *</span>
                      </label>
                      <input
                        type="email"
                        className="input input-bordered w-full"
                        placeholder="you@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />
                    </div>

                    <div>
                      <label className="label">
                        <span className="label-text font-medium">Phone Number</span>
                      </label>
                      <input
                        type="tel"
                        className="input input-bordered w-full"
                        placeholder="(555) 555-5555"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                      />
                    </div>

                    <div>
                      <label className="label">
                        <span className="label-text font-medium">Message *</span>
                      </label>
                      <textarea
                        className="textarea textarea-bordered w-full h-32"
                        placeholder="How can we help you?"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        required
                        minLength={10}
                      />
                    </div>

                    <div ref={turnstileRef} className="flex justify-center" />

                    {errorMsg && (
                      <div className="alert alert-error text-sm">
                        <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <span>{errorMsg}</span>
                      </div>
                    )}

                    <button
                      type="submit"
                      className="btn btn-primary w-full"
                      disabled={status === 'submitting'}
                    >
                      {status === 'submitting' ? (
                        <span className="loading loading-spinner loading-sm" />
                      ) : (
                        'Send Message'
                      )}
                    </button>
                  </form>
                </div>
              </div>
            )}

            <div className="text-center mt-8 text-base-content/60 text-sm">
              <p>Or email us directly at <a href="mailto:contact@changelyric.com" className="link link-primary">contact@changelyric.com</a></p>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-base-200 py-8 border-t border-base-300">
        <div className="container mx-auto px-4">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <Link href="/privacy-policy" className="link link-hover">Privacy</Link>
              <Link href="/tos" className="link link-hover">Terms</Link>
              <Link href="/" className="link link-hover">Home</Link>
            </div>
            <div className="text-sm text-base-content/60">
              &copy; {new Date().getFullYear()} ChangeLyric
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
